import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { cva } from 'class-variance-authority';
import React from 'react';

/** A single selectable segment. */
export interface SegmentedControlOption<T extends string = string> {
  /** Value emitted through `onChange` when the segment is selected. */
  value: T;
  /** Primary label rendered on the first line. */
  label: string;
  /** Optional secondary line rendered under the label. */
  description?: string;
  /** Disables selection of this segment. */
  disabled?: boolean;
}

/**
 * Visual theme of the control.
 * - `default` — light surface tokens, for use on content backgrounds
 *   (modals, panels).
 * - `navigation` — navigation tokens, for use inside the app header switcher.
 */
export type SegmentedControlVariant = 'default' | 'navigation';

interface SegmentedControlProps<T extends string = string> {
  /** Segments to render, left to right. */
  options: SegmentedControlOption<T>[];
  /** Currently selected value. */
  value: T;
  /** Called with the value of the clicked segment. */
  onChange: (value: T) => void;
  className?: string;
  /** Visual theme. Defaults to `default` (content surfaces). */
  variant?: SegmentedControlVariant;
}

const segmentVariants = cva(
  'flex min-w-0 flex-1 cursor-pointer appearance-none flex-col items-center justify-center gap-0.5 rounded-full border-0 bg-transparent px-4 py-2 text-center transition-colors duration-[256ms] ease-main outline-none disabled:pointer-events-none disabled:opacity-40',
  {
    variants: {
      variant: {
        default: '',
        navigation: '',
      },
      active: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      { variant: 'default', active: true, class: 'bg-bg-content' },
      { variant: 'navigation', active: true, class: 'bg-navigation-surface' },
    ],
    defaultVariants: {
      variant: 'default',
      active: false,
    },
  },
);

/** Per-variant text color tokens for the label/description lines. */
const textColorTokens: Record<
  SegmentedControlVariant,
  {
    label: { active: string; inactive: string };
    description: { active: string; inactive: string };
  }
> = {
  default: {
    label: { active: 'text-text-primary', inactive: 'text-text-tertiary' },
    description: {
      active: 'text-text-secondary',
      inactive: 'text-text-tertiary',
    },
  },
  navigation: {
    label: {
      active: 'text-navigation-text-primary',
      inactive: 'text-navigation-text-tertiary',
    },
    description: {
      active: 'text-navigation-text-secondary',
      inactive: 'text-navigation-text-tertiary',
    },
  },
};

/**
 * Pill-style segmented control with an optional secondary line per segment.
 * Text uses Typography variants and colors resolve through design tokens, so
 * it adapts to every company theme.
 */
function SegmentedControlInner<T extends string>(
  {
    options,
    value,
    onChange,
    className,
    variant = 'default',
  }: SegmentedControlProps<T>,
  ref: React.Ref<HTMLDivElement>,
) {
  const colors = textColorTokens[variant];

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const enabled = options.filter((option) => !option.disabled);
    if (enabled.length === 0) {
      return;
    }
    const currentIndex = enabled.findIndex((option) => option.value === value);
    let nextIndex: number;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % enabled.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + enabled.length) % enabled.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = enabled.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const next = enabled[nextIndex];
    if (!next || next.value === value) {
      return;
    }
    onChange(next.value);
    event.currentTarget
      .querySelector<HTMLButtonElement>(
        `[data-value="${CSS.escape(next.value)}"]`,
      )
      ?.focus();
  };

  return (
    <div
      ref={ref}
      role="tablist"
      data-slot="segmented-control"
      onKeyDown={handleKeyDown}
      className={cn(
        'flex w-full items-center gap-1 rounded-full bg-surface-transparent-3 p-1',
        className,
      )}
    >
      {options.map((option) => {
        const isActive = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={option.disabled}
            tabIndex={isActive ? 0 : -1}
            data-slot="segment"
            data-active={isActive}
            data-value={option.value}
            onClick={() => onChange(option.value)}
            className={cn(segmentVariants({ variant, active: isActive }))}
          >
            <Typography.Text200Semibold
              as="span"
              className={cn(
                'w-full truncate',
                isActive ? colors.label.active : colors.label.inactive,
              )}
            >
              {option.label}
            </Typography.Text200Semibold>
            {option.description && (
              <Typography.Text100Regular
                as="span"
                className={cn(
                  'w-full truncate',
                  isActive
                    ? colors.description.active
                    : colors.description.inactive,
                )}
              >
                {option.description}
              </Typography.Text100Regular>
            )}
          </button>
        );
      })}
    </div>
  );
}

export const SegmentedControl = React.forwardRef(SegmentedControlInner) as <
  T extends string,
>(
  props: SegmentedControlProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement;

export { segmentVariants };
